// Core
import React from 'react';

// Libraries
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Store
import { getUser } from '../../../../../../store/session.store';

// Plugins
import { timestampToDateStrText, timestampToTimeAgo } from '../../../../../../plugins/general';
import {
  countActiveAlarms,
  formatCrusherModel,
  isDeviceDisconnected,
  isEnabled,
  runningStatusToEquipmentStatus,
  runningStatusToText,
} from '../../../../../../plugins/crusher';

// Types
import { Theme } from '../../../../../../types/theme';
import { CrusherModel } from '../../../../../../types/crusher';
import {
  CrusherDrawerType,
  EdgeDeviceOperationalStatusStatus,
  User,
  UserType,
} from '../../../../../../types/global';

// Media
import CrusherImage from '../../../../../../media/images/equipment/p400.avif';

// Components
import {
  ConnectionStatus,
  ConnectionStatusStatus,
} from '../../../../../../components/ConnectionStatus';
import { EquipmentStatusTag } from '../../../../../../components/EquipmentStatusTag';
import {
  Button,
  ButtonSize,
  ButtonType,
} from '../../../../../../layout/elements/Button';
import { Breadcrumb, BreadcrumbType } from '../../../../../../layout/elements/Breadcrumb';
import {
  Paragraph,
  ParagraphAlign,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import {
  Title,
  TitleSize,
  TitleWeight,
} from '../../../../../../layout/elements/Title';
import { Tooltip } from '../../../../../../layout/elements/Tooltip';
import {
  Alarm,
  CircleInfo,
  DownloadLine,
  Information,
  LineChart,
  MapOutline,
  Settings,
  Tools,
} from '../../../../../../layout/icons';
import { ViewType } from '../../../../../../components/CameraFeed';

// View
import {
  AlarmButton,
  ButtonContainer,
  CloudConnectionContainer,
  CloudConnectionContent,
  ContentContainer,
  CrusherInfoContainer,
  CrusherStatus,
  DetailsContainer,
  EquipmentImageContainer,
  EquipmentImageContainerBelow,
  EquipmentName,
  EquipmentStatusContainer,
  LastCloudConnection,
  LastUpdateTime,
  OverviewContainer,
  OverviewItemsContainer,
  OverviewItemsWrapper,
  OverviewWrapper,
  RegisteredMark,
  TooltipIconContainer,
} from './Details.style';
import { DetailsProps } from './Details.types';

function Details({
  breadcrumbItems,
  className,
  equipmentUnitCrusher,
  unitData,
  edgeStatus,
  onDrawerOpen,
  onModalOpen,
  testId,
}: DetailsProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const {
    isXs,
    isSm,
    isMd,
    isLg,
  } = useBreakpoint();

  // User context
  const user: User | null = getUser();

  // Responsive conditions
  const isMobileOrTablet: boolean = isXs || isSm || isMd || isLg;
  const isDesktop: boolean = !isMobileOrTablet;

  // Check if at least one vibration sensor is enabled
  const hasVibrationSensorEnabled: boolean = unitData
    ? (
      isEnabled(unitData.vibrationSensor1Enable)
      || isEnabled(unitData.vibrationSensor2Enable)
      || isEnabled(unitData.vibrationSensor3Enable)
      || isEnabled(unitData.vibrationSensor4Enable)
    )
    : false;

  // Count active alarms from the last unit data
  const activeAlarms: number = countActiveAlarms(unitData);
  const hasActiveAlarms: boolean = activeAlarms > 0;

  // Check if device is connected and edge status is on
  const isEdgeRunning: boolean = edgeStatus?.edgeStatus === EdgeDeviceOperationalStatusStatus.on
    && !isDeviceDisconnected(unitData?.timestamp);

  // Only apply animation if there are active alarms AND device is connected
  // (not in gray screen state)
  const shouldApplyAnimation: boolean = hasActiveAlarms && isEdgeRunning;

  // Function to format the equipment title
  const formattedTitle = (): JSX.Element => {
    const formatModelWithJSX = (model: CrusherModel): JSX.Element => {
      const formatted: string = formatCrusherModel(model);

      if (formatted.includes('®')) {
        const parts: string[] = formatted.split('®');
        return (
          <>
            {parts[0]}
            <RegisteredMark>
              ®
            </RegisteredMark>
            {parts[1]}
          </>
        );
      }

      return <span>{formatted}</span>;
    };

    return (
      isXs ? (
        <Paragraph
          size={ParagraphSize.lg}
          weight={ParagraphWeight.bold}
          color={theme.color.emptyBox.titleColor}
        >
          {equipmentUnitCrusher.customerUnitName
            || formatModelWithJSX(equipmentUnitCrusher.model)}
        </Paragraph>
      ) : (
        <Title size={TitleSize.xxs} weight={TitleWeight.bold} className="title">
          {equipmentUnitCrusher.customerUnitName || formatModelWithJSX(equipmentUnitCrusher.model)}
        </Title>
      )
    );
  };

  return (
    <DetailsContainer
      className={className}
      data-testid={testId}
      hasActiveAlarms={shouldApplyAnimation}
    >
      <ContentContainer>
        <Row>
          <Breadcrumb
            type={BreadcrumbType.plant}
            items={breadcrumbItems}
            testId="breadcrumb"
          />
        </Row>
        <CrusherStatus>
          <EquipmentStatusContainer>
            <EquipmentStatusTag
              status={
                runningStatusToEquipmentStatus(
                  unitData?.unitStatus,
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )
              }
              text={runningStatusToText(unitData?.unitStatus)}
              testId="equipment-status"
            />
            {isMobileOrTablet && (
              <Tooltip text={t('common.alarm.plural')}>
                <AlarmButton
                  size={ButtonSize.xs}
                  square
                  className="button-actions"
                  type={ButtonType.secondary}
                  testId="alarm-button-mobile"
                  onClick={() => onDrawerOpen(CrusherDrawerType.alarms)}
                  hasActiveAlarms={shouldApplyAnimation}
                >
                  <Alarm />
                  {
                    activeAlarms > 0 && (
                      <div className="alarms-indicator" data-testid="alarm-indicator-mobile">
                        {activeAlarms}
                      </div>
                    )
                  }
                </AlarmButton>
              </Tooltip>
            )}
          </EquipmentStatusContainer>
          <EquipmentName>
            {formattedTitle()}
          </EquipmentName>

          <Paragraph
            size={ParagraphSize.sm}
            color={theme.color.crusherDashboard.detailsModule.lastUpdateTextColor}
            testId="last-update-item"
          >
            <Trans
              i18nKey="common.lastUpdate"
              values={{ lastUpdate: timestampToTimeAgo(unitData?.timestamp) }}
              components={{ lastUpdateTime: <LastUpdateTime /> }}
            />
          </Paragraph>

        </CrusherStatus>

        {!hasVibrationSensorEnabled && isDesktop && (
          <EquipmentImageContainerBelow>
            <img
              src={CrusherImage}
              alt={formatCrusherModel(equipmentUnitCrusher.model)}
              data-testid="equipment-model-image"
            />
          </EquipmentImageContainerBelow>
        )}

        <OverviewWrapper>
          <OverviewContainer>
            <Paragraph weight={ParagraphWeight.bold} className="overview-section">
              {t('common.overview')}
            </Paragraph>

            <OverviewItemsContainer>
              <OverviewItemsWrapper>
                <CloudConnectionContainer>
                  <CloudConnectionContent>
                    <Paragraph
                      size={ParagraphSize.sm}
                      color={theme.color.crusherDashboard.detailsModule.parametersTextColor}
                      className="paragraph-sm"
                    >
                      {t('common.cloudConnection')}
                    </Paragraph>
                    <Tooltip
                      text={(
                        <Trans
                          i18nKey="common.lastUpdate"
                          values={{ lastUpdate: timestampToTimeAgo(edgeStatus?.timestamp) }}
                          components={{ lastUpdateTime: <LastCloudConnection /> }}
                        />
                      )}
                    >
                      <TooltipIconContainer>
                        <CircleInfo />
                      </TooltipIconContainer>
                    </Tooltip>
                  </CloudConnectionContent>
                  <ConnectionStatus
                    status={
                      edgeStatus?.edgeStatus === EdgeDeviceOperationalStatusStatus.on
                        ? ConnectionStatusStatus.online
                        : ConnectionStatusStatus.offline
                    }
                    testId="connection-status"
                    className="connection-status"
                  />
                </CloudConnectionContainer>

                <CrusherInfoContainer>
                  <Paragraph
                    size={ParagraphSize.sm}
                    color={theme.color.crusherDashboard.detailsModule.parametersTextColor}
                    className="paragraph-sm"
                  >
                    {t('common.superiorPartNumber')}
                  </Paragraph>
                  <Paragraph
                    size={ParagraphSize.sm}
                    weight={ParagraphWeight.medium}
                    align={ParagraphAlign.right}
                    color={theme.color.crusherDashboard.detailsModule.valuesTextColor}
                    className="paragraph-sm"
                  >
                    {formatCrusherModel(equipmentUnitCrusher.model)}
                  </Paragraph>
                </CrusherInfoContainer>

                <CrusherInfoContainer>
                  <Paragraph
                    size={ParagraphSize.sm}
                    className="paragraph-sm"
                    color={theme.color.crusherDashboard.detailsModule.parametersTextColor}
                  >
                    {t('common.serialNumber')}
                  </Paragraph>
                  <Paragraph
                    size={ParagraphSize.sm}
                    weight={ParagraphWeight.medium}
                    align={ParagraphAlign.right}
                    color={theme.color.crusherDashboard.detailsModule.valuesTextColor}
                    className="paragraph-sm"
                  >
                    {equipmentUnitCrusher.serialNumber}
                  </Paragraph>
                </CrusherInfoContainer>

                <CrusherInfoContainer>
                  <Paragraph
                    size={ParagraphSize.sm}
                    color={theme.color.crusherDashboard.detailsModule.parametersTextColor}
                    className="paragraph-sm"
                  >
                    {t('common.installedAt')}
                  </Paragraph>
                  <Paragraph
                    size={ParagraphSize.sm}
                    weight={ParagraphWeight.medium}
                    align={ParagraphAlign.right}
                    color={theme.color.crusherDashboard.detailsModule.valuesTextColor}
                    className="paragraph-sm"
                  >
                    {timestampToDateStrText(equipmentUnitCrusher.installedAt)}
                  </Paragraph>
                </CrusherInfoContainer>
              </OverviewItemsWrapper>
            </OverviewItemsContainer>
          </OverviewContainer>

          {(isMd || isLg) && (
            <EquipmentImageContainer>
              <img
                src={CrusherImage}
                alt={formatCrusherModel(equipmentUnitCrusher.model)}
                data-testid="equipment-model-image"
              />
            </EquipmentImageContainer>
          )}
        </OverviewWrapper>

        {isMobileOrTablet ? (
          <>
            <ButtonContainer>
              <Button
                size={ButtonSize.sm}
                type={ButtonType.secondary}
                testId="trends-button-mobile"
                onClick={() => onDrawerOpen(CrusherDrawerType.trending)}
              >
                <LineChart />
                {t('common.trends')}
              </Button>
              <Button
                size={ButtonSize.sm}
                type={ButtonType.secondary}
                testId="view-camera-feed-button"
                onClick={(): void => onModalOpen(ViewType.map)}
              >
                <MapOutline />
                {t('common.viewMap')}
              </Button>
            </ButtonContainer>
            <ButtonContainer>
              <Button
                size={ButtonSize.sm}
                type={ButtonType.secondary}
                testId="maintenance-button-mobile"
                onClick={() => onDrawerOpen(CrusherDrawerType.maintenance)}
              >
                <Tools />
                {t('common.maintenance')}
              </Button>
            </ButtonContainer>
          </>
        ) : (
          <ButtonContainer>
            <Tooltip className="tooltip" text={t('common.settings')}>
              <Button
                square
                className="button-actions"
                size={ButtonSize.xs}
                disabled
                type={ButtonType.secondary}
                testId="settings-button"
                onClick={(): void => {}}
              >
                <Settings />
              </Button>
            </Tooltip>
            <Tooltip className="tooltip" text={t('common.trending')}>
              <Button
                square
                className="button-actions"
                size={ButtonSize.xs}
                type={ButtonType.secondary}
                testId="trending-button"
                onClick={() => onDrawerOpen(CrusherDrawerType.trending)}
              >
                <LineChart />
              </Button>
            </Tooltip>
            <Tooltip className="tooltip" text={t('common.maintenance')}>
              <Button
                square
                className="button-actions"
                size={ButtonSize.xs}
                type={ButtonType.secondary}
                testId="maintenance-button"
                onClick={() => onDrawerOpen(CrusherDrawerType.maintenance)}
              >
                <Tools />
              </Button>
            </Tooltip>
            {user?.type === UserType.admin && !isMobileOrTablet && (
              <Tooltip className="tooltip" text={t('common.export')}>
                <Button
                  square
                  className="button-actions"
                  size={ButtonSize.xs}
                  type={ButtonType.secondary}
                  testId="export-button"
                  onClick={() => onDrawerOpen(CrusherDrawerType.export)}
                >
                  <DownloadLine />
                </Button>
              </Tooltip>
            )}
            <Tooltip className="tooltip" text={t('common.info')}>
              <Button
                square
                disabled
                className="button-actions"
                size={ButtonSize.xs}
                type={ButtonType.secondary}
                testId="info-button"
                onClick={(): void => {}}
              >
                <Information />
              </Button>
            </Tooltip>
            <Tooltip className="tooltip" text={t('common.alarm.plural')}>
              <AlarmButton
                size={ButtonSize.xs}
                square
                className="button-actions"
                type={ButtonType.secondary}
                testId="alarm-button"
                onClick={() => onDrawerOpen(CrusherDrawerType.alarms)}
                hasActiveAlarms={shouldApplyAnimation}
              >
                <Alarm />
                {activeAlarms > 0 && (
                  <div className="alarms-indicator" data-testid="alarm-indicator">
                    {activeAlarms}
                  </div>
                )}
              </AlarmButton>
            </Tooltip>
          </ButtonContainer>
        )}
      </ContentContainer>
    </DetailsContainer>
  );
}

export { Details };
